import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import Translate from "../../../../shared/translation/Translate";
import Icon from "../../../../shared/components/Icon";
import {Dropdown} from "primereact/dropdown";
import {
	CompressObjects,
	CompressProfile,
	CompressSettings,
	PdfaEditProfile,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import Exception from "../../../../shared/exception/Exception";
import MessageBox from "../../../../shared/components/MessageBox";
import {MessageType} from "../../../../shared/log/MessageInterface";

export interface DebugGlobals {
	objectStreamSize: number;
	compressProfile: CompressProfile;
	signatureEditProfile: SignatureEditProfile;
	pdfaEditProfile: PdfaEditProfile;
	portalResolution: number;
	documentCache: number;
	concurrentOperations: number;
	checkPdfPasswords: boolean;
	sharepointOnlineEnabled: boolean;
	documentChecks: boolean;
}

export function initDebugGlobals(): DebugGlobals {
	return {
		signatureEditProfile: SignatureEditProfile.AdobeRestrictions,
		pdfaEditProfile: PdfaEditProfile.RemovePdfaMetadata,
		portalResolution: 96,
		compressProfile: CompressSettings.getCompressProfileDefault(),
		objectStreamSize: CompressObjects.getObjectStreamSizeDefault(),
		documentCache: 3,
		concurrentOperations: 1,
		checkPdfPasswords: true,
		sharepointOnlineEnabled: false,
		documentChecks: true
	};
}

interface DebugDialogProperties extends AbstractDialogProperties, DebugGlobals {
}

interface DebugDialogState extends AbstractDialogState {
	throwError: boolean;
}

export class DebugDialogDefinition extends AbstractDialog<DebugDialogProperties, DebugDialogState> {
	static displayName = "DebugDialog";

	constructor(props: Readonly<DebugDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			throwError: false
		};
	}

	/**
	 * render component as react node
	 */
	public renderDialog(): ReactNode {
		if (this.state.throwError) {
			throw new Exception("Render exception");
		}

		let classList: Array<string> = ["debug-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<div className="p-col-12">
						<MessageBox {...this.props} type={MessageType.WARNING} closeable={true}>
							<Translate {...this.props} data="debug.warning" namespace="dialog"/>
						</MessageBox>
					</div>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-signature-profile">
								<Translate {...this.props} data="debug.signature.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Dropdown value={this.props.signatureEditProfile} inputId="control-signature-profile"
										  options={[
											  {
												  label: Translate.value(
													  "debug.signature.NoEditRestrictions", {ns: "dialog"}
												  ),
												  value: SignatureEditProfile.NoEditRestrictions
											  },
											  {
												  label: Translate.value(
													  "debug.signature.GeneralEditRestrictions", {ns: "dialog"}
												  ),
												  value: SignatureEditProfile.GeneralEditRestrictions
											  },
											  {
												  label: Translate.value(
													  "debug.signature.AdobeRestrictions", {ns: "dialog"}
												  ),
												  value: SignatureEditProfile.AdobeRestrictions
											  }
										  ]}
										  onChange={(e) =>
											  this.props.context.getPage().setState({
												  signatureEditProfile: e.value
											  })
										  }
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-pdfa-profile">
								<Translate {...this.props} data="debug.pdfa.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Dropdown value={this.props.pdfaEditProfile} inputId="control-pdfa-profile"
										  options={[
											  {
												  label: Translate.value(
													  "debug.pdfa.NoEditRestrictions", {ns: "dialog"}
												  ),
												  value: PdfaEditProfile.NoEditRestrictions
											  },
											  {
												  label: Translate.value(
													  "debug.pdfa.GeneralEditRestrictions", {ns: "dialog"}
												  ),
												  value: PdfaEditProfile.GeneralEditRestrictions
											  },
											  {
												  label: Translate.value(
													  "debug.pdfa.RemovePdfaMetadata", {ns: "dialog"}
												  ),
												  value: PdfaEditProfile.RemovePdfaMetadata
											  }
										  ]}
										  onChange={(e) =>
											  this.props.context.getPage().setState({
												  pdfaEditProfile: e.value
											  })
										  }
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-ppi">
								<Translate {...this.props} data="debug.ppi" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-ppi" step={1} suffix=" ppi" showButtons={true}
											 value={this.props.portalResolution} useGrouping={false}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 portalResolution: e.value
												 })
											 }/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-compress-profile">
								<Translate {...this.props} data="debug.compress_profile.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Dropdown value={this.props.compressProfile} inputId="control-compress-profile"
										  options={[
											  {
												  label: Translate.value(
													  "debug.compress_profile.never", {ns: "dialog"}
												  ),
												  value: CompressProfile.Never
											  },
											  {
												  label: Translate.value(
													  "debug.compress_profile.always", {ns: "dialog"}
												  ),
												  value: CompressProfile.Always
											  },
											  {
												  label: Translate.value(
													  "debug.compress_profile.recompress", {ns: "dialog"}
												  ),
												  value: CompressProfile.Recompress
											  }
										  ]}
										  onChange={(e) =>
											  this.props.context.getPage().setState({
												  compressProfile: e.value
											  })
										  }
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-object-stream-size">
								<Translate {...this.props} data="debug.object_stream_size" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-object-stream-size" step={1} showButtons={true}
											 value={this.props.objectStreamSize} useGrouping={false}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 objectStreamSize: e.value
												 })
											 }/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-document-cache">
								<Translate {...this.props} data="debug.document_cache" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-document-cache" step={1} showButtons={true}
											 value={this.props.documentCache} useGrouping={false}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 documentCache: e.value
												 })
											 }/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-concurrent-operations">
								<Translate {...this.props} data="debug.concurrent_operations" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-concurrent-operations" step={1} showButtons={true}
											 value={this.props.concurrentOperations} useGrouping={false}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 concurrentOperations: e.value
												 })
											 }/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-document-checks">
								<Translate {...this.props} data="debug.document_checks" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-document-checks"
											 checked={this.props.documentChecks}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 documentChecks: e.value
												 })
											 }/>
							</div>
						</div>
						{this.props.documentChecks &&
							<div className="p-col-12 p-grid">
								<label className="p-col-12 p-sm-4" htmlFor="control-check-passwords">
									<Translate {...this.props} data="debug.check_passwords" namespace="dialog"/>
								</label>
								<div className="p-col-12 p-sm-8 switch-container">
									<InputSwitch inputId="control-check-passwords"
												 checked={this.props.checkPdfPasswords}
												 onChange={(e) =>
													 this.props.context.getPage().setState({
														 checkPdfPasswords: e.value
													 })
												 }/>
								</div>
							</div>
						}
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-sharepoint-online">
								<Translate {...this.props} data="debug.sharepoint_online" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-sharepoint-online"
											 checked={this.props.sharepointOnlineEnabled}
											 onChange={(e) =>
												 this.props.context.getPage().setState({
													 sharepointOnlineEnabled: e.value
												 })
											 }/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor="control-concurrent-operations"/>
							<div className="p-col-12 p-sm-8 switch-container">
								<Button label={Translate.value("debug.test_error", {ns: "dialog"})}
										icon="icon icon-bug" className="dialog-button"
										onClick={() => {
											// throw render error
											this.setState({
												throwError: true
											});

											// throw console error
											console.error("console error");

											// throw js exception
											throw new Exception("JS code exception");
										}}/>
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-bug"}/>
					<Translate {...this.props} data="debug.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.close")} icon="icon icon-close"
						onClick={() => this.hide()} className="dialog-button"/>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}
}

const DebugDialog = React.memo(DebugDialogDefinition,
	(prevProps: Readonly<DebugDialogProperties>, nextProps: Readonly<DebugDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DebugDialog;